interface Point {
  x: number
  y: number
}

interface Shape {
  draw(ctx: CanvasRenderingContext2D): void
  containsPoint(point: Point): boolean
  getNearestEditPoint(mousePosition: Point): Point | null
}

export class CanvasEditor {
  canvas: HTMLCanvasElement
  ctx: CanvasRenderingContext2D
  shapes: Shape[]
  selectedShape: Shape | null
  mousePosition: Point
  draggingPoint: Point | null
  // 新增属性
  private leafletMap: L.Map | undefined

  constructor(canvas: HTMLCanvasElement, leafletMap?: L.Map) {
    this.canvas = canvas
    this.ctx = this.canvas.getContext('2d')!
    this.shapes = []
    this.selectedShape = null
    this.mousePosition = { x: 0, y: 0 }
    this.draggingPoint = null
    this.leafletMap = leafletMap

    canvas.addEventListener('mousedown', this.handleMouseDown.bind(this))
    canvas.addEventListener('mousemove', this.handleMouseMove.bind(this))
    canvas.addEventListener('mouseup', this.handleMouseUp.bind(this))
  }

  // 新增方法
  disableLeafletDrag() {
    if (this.leafletMap) {
      this.leafletMap.dragging.disable()
    }
  }

  enableLeafletDrag() {
    if (this.leafletMap) {
      this.leafletMap.dragging.enable()
    }
  }

  addShape(shape: Shape) {
    this.shapes.push(shape)
    this.redraw()
  }

  removeShape(shape: Shape) {
    const index = this.shapes.indexOf(shape)
    if (index !== -1) {
      this.shapes.splice(index, 1)
      this.redraw()
    }
  }

  redraw() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.shapes.forEach((shape) => shape.draw(this.ctx))
  }

  private handleMouseDown(e: MouseEvent) {
    const rect = this.canvas.getBoundingClientRect()
    this.mousePosition = { x: e.clientX - rect.left, y: e.clientY - rect.top }

    for (const shape of this.shapes) {
      if (shape.containsPoint(this.mousePosition)) {
        this.selectedShape = shape
        break
      }
    }

    if (this.selectedShape) {
      this.disableLeafletDrag() // 禁用地图拖拽
      const selectedPoint = this.selectedShape.getNearestEditPoint(
        this.mousePosition
      )
      if (selectedPoint) {
        this.draggingPoint = selectedPoint
        this.canvas.style.cursor = 'move'
      } else {
        this.canvas.style.cursor = 'grab'
      }
    } else {
      this.canvas.style.cursor = 'default'
    }
  }

  private handleMouseMove(e: MouseEvent) {
    if (this.draggingPoint) {
      const rect = this.canvas.getBoundingClientRect()
      const newX = e.clientX - rect.left
      const newY = e.clientY - rect.top
      this.draggingPoint.x = newX
      this.draggingPoint.y = newY
      this.redraw()
    }
  }

  private handleMouseUp() {
    this.draggingPoint = null
    this.canvas.style.cursor = 'default'
    if (this.leafletMap) {
      this.enableLeafletDrag() // 恢复地图拖拽
    }
  }
}

export class EditableRectangle implements Shape {
  points: Point[]

  constructor(x: number, y: number, width: number, height: number) {
    this.points = [
      { x, y },
      { x: x + width, y },
      { x: x + width, y: y + height },
      { x, y: y + height },
    ]
  }

  draw(ctx: CanvasRenderingContext2D) {
    ctx.beginPath()
    ctx.moveTo(this.points[0].x, this.points[0].y)
    for (let i = 1; i < this.points.length; i++) {
      ctx.lineTo(this.points[i].x, this.points[i].y)
    }
    ctx.closePath()
    ctx.stroke()

    this.points.forEach((point) => {
      ctx.beginPath()
      ctx.arc(point.x, point.y, 5, 0, Math.PI * 2)
      ctx.fill()
    })
  }

  containsPoint(point: Point): boolean {
    const minX = Math.min(...this.points.map((p) => p.x))
    const maxX = Math.max(...this.points.map((p) => p.x))
    const minY = Math.min(...this.points.map((p) => p.y))
    const maxY = Math.max(...this.points.map((p) => p.y))

    return (
      point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY
    )
  }

  getNearestEditPoint(mousePosition: Point): Point | null {
    let nearestPoint: Point | null = null
    let minDistance = Number.POSITIVE_INFINITY
    for (const point of this.points) {
      const distance = Math.hypot(
        mousePosition.x - point.x,
        mousePosition.y - point.y
      )
      if (distance < minDistance) {
        minDistance = distance
        nearestPoint = point
      }
    }
    return nearestPoint
  }
}
